/**
 * MaskPanel — C013 Data Masking Demo
 * R12 #319: Client-side validation before API call + better error parsing
 * R12 #320: Type-constrained mode select (MASKING_POLICY map)
 */
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useMaskDemo } from '../hooks/useMaskDemo';
import { maskFormSchema, type MaskFormValues } from '../schema/showcaseSchema';
import type { MaskType, MaskingMode } from '../types/showcase';

// #419: canonical MaskType values — must match BE MaskType enum exactly
const MASK_TYPES: { value: MaskType; label: string; example: string }[] = [
  { value: 'EMAIL',       label: 'EMAIL',       example: 'user@example.com' },
  { value: 'PHONE_KR',    label: 'PHONE_KR',    example: '010-1234-5678' },
  { value: 'RESIDENT_NO', label: 'RESIDENT_NO', example: '900101-1234567' },
  { value: 'CARD_NO',     label: 'CARD_NO',     example: '1234-5678-9012-3456' },
  { value: 'ACCOUNT_NO',  label: 'ACCOUNT_NO',  example: '123-456-789012' },
  { value: 'NAME_KR',     label: 'NAME_KR',     example: '홍길동' },
  { value: 'ADDRESS_KR',  label: 'ADDRESS_KR',  example: '서울특별시 강남구 테헤란로 152' },
  { value: 'IP_V4',       label: 'IP_V4',       example: '192.168.1.100' },
  { value: 'BIRTH_DATE',  label: 'BIRTH_DATE',  example: '1990-01-01' },
  { value: 'JWT',         label: 'JWT',         example: 'eyJhbGciOiJIUzI1NiJ9.payload.sig' },
  { value: 'BUSINESS_NO', label: 'BUSINESS_NO', example: '123-45-67890' },
  { value: 'CUSTOM',      label: 'CUSTOM',      example: 'AB-12345-XY' },
];

/**
 * #419 #320: Static masking policy map — mirrors BE MASK_POLICY exactly.
 * Mode options are constrained by selected type.
 * BE MaskingMode enum: LIGHT | DEFAULT | STRICT (no PARTIAL).
 */
const MASKING_POLICY: Record<MaskType, MaskingMode[]> = {
  EMAIL:       ['LIGHT', 'DEFAULT', 'STRICT'],
  PHONE_KR:    ['LIGHT', 'DEFAULT', 'STRICT'],
  RESIDENT_NO: ['DEFAULT', 'STRICT'],
  CARD_NO:     ['LIGHT', 'DEFAULT', 'STRICT'],
  ACCOUNT_NO:  ['DEFAULT', 'STRICT'],
  NAME_KR:     ['DEFAULT', 'STRICT'],
  ADDRESS_KR:  ['DEFAULT', 'STRICT'],
  IP_V4:       ['DEFAULT', 'STRICT'],
  BIRTH_DATE:  ['DEFAULT', 'STRICT'],
  JWT:         ['STRICT'],
  BUSINESS_NO: ['DEFAULT', 'STRICT'],
  CUSTOM:      ['LIGHT', 'DEFAULT', 'STRICT'],
};

// #419: BE MaskingMode enum: LIGHT | DEFAULT | STRICT
const ALL_MODES: { value: MaskingMode; labelKey: string }[] = [
  { value: 'LIGHT',   labelKey: 'showcase.mask.modes.LIGHT' },
  { value: 'DEFAULT', labelKey: 'showcase.mask.modes.DEFAULT' },
  { value: 'STRICT',  labelKey: 'showcase.mask.modes.STRICT' },
];

export function MaskPanel() {
  const { t } = useTranslation();
  const { mutate, isPending, data, error, isError, isSuccess, reset } = useMaskDemo();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isValid },
  } = useForm<MaskFormValues>({
    resolver: zodResolver(maskFormSchema),
    defaultValues: { type: 'EMAIL', mode: 'DEFAULT', value: '' },
  });

  const selectedType = watch('type');
  const selectedMode = watch('mode');
  const currentExample = MASK_TYPES.find((m) => m.value === selectedType)?.example ?? '';
  const allowedModes = MASKING_POLICY[selectedType] ?? ['DEFAULT'];

  // #320: Reset mode to first allowed when type changes
  useEffect(() => {
    if (!allowedModes.includes(selectedMode)) {
      setValue('mode', allowedModes[0]);
    }
  }, [selectedType, allowedModes, selectedMode, setValue]);

  // #319: Client-side combo validation (empty value already handled by zod, but extra guard)
  const [comboError, setComboError] = useState<string | null>(null);

  function onSubmit(values: MaskFormValues) {
    // #319: Guard — should not happen since mode is constrained, but extra safety
    const allowed = MASKING_POLICY[values.type] ?? ['DEFAULT'];
    if (!allowed.includes(values.mode)) {
      setComboError(t('showcase.mask.invalidCombo', { type: values.type, mode: values.mode }));
      return;
    }
    setComboError(null);
    mutate({ type: values.type, mode: values.mode, value: values.value });
  }

  // #319: Better error parsing — surface BE error code/message
  const displayError = (() => {
    if (!isError || !error) return null;
    // error is ApiError from useMaskDemo
    return { code: error.code ?? 'ERROR', message: error.message ?? t('showcase.mask.unknownError') };
  })();

  return (
    <section aria-labelledby="mask-panel-title">
      <h2
        id="mask-panel-title"
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
          marginBottom: 'var(--spacing-4)',
          marginTop: 0,
        }}
      >
        {t('showcase.mask.title')}
      </h2>
      <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--spacing-6)' }}>
        {t('showcase.mask.subtitle')}
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* MaskType selector */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="mask-type"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.mask.type')}
          </label>
          <select
            id="mask-type"
            aria-label={t('showcase.mask.type')}
            aria-invalid={!!errors.type}
            {...register('type')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.type ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
            }}
          >
            {MASK_TYPES.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {/* #320: MaskingMode — constrained to allowed modes for selected type */}
        <fieldset style={{ border: 'none', padding: 0, margin: '0 0 var(--spacing-4)' }}>
          <legend
            style={{
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.mask.mode')}
            <span
              style={{
                marginLeft: 'var(--spacing-2)',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                fontWeight: 400,
              }}
            >
              ({t('showcase.mask.modeConstraintHint', { type: selectedType })})
            </span>
          </legend>
          <div style={{ display: 'flex', gap: 'var(--spacing-3)', flexWrap: 'wrap' }}>
            {ALL_MODES.map((m) => {
              const isAllowed = allowedModes.includes(m.value);
              const current = selectedMode;
              return (
                <label
                  key={m.value}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-2)',
                    padding: 'var(--spacing-2) var(--spacing-3)',
                    border: `1px solid ${
                      current === m.value
                        ? 'var(--color-brand-primary)'
                        : 'var(--color-surface-border)'
                    }`,
                    borderRadius: 'var(--radius-md)',
                    cursor: isAllowed ? 'pointer' : 'not-allowed',
                    fontSize: 'var(--font-size-sm)',
                    background:
                      current === m.value
                        ? 'var(--color-sidebar-active-bg)'
                        : isAllowed
                        ? 'transparent'
                        : 'var(--color-surface-muted)',
                    color:
                      current === m.value
                        ? 'var(--color-brand-primary)'
                        : isAllowed
                        ? 'var(--color-text-primary)'
                        : 'var(--color-text-muted)',
                    opacity: isAllowed ? 1 : 0.5,
                  }}
                  title={isAllowed ? t(m.labelKey) : t('showcase.mask.modeNotAllowed', { mode: m.value, type: selectedType })}
                >
                  <input
                    type="radio"
                    value={m.value}
                    disabled={!isAllowed || isPending}
                    {...register('mode')}
                    style={{ accentColor: 'var(--color-brand-primary)' }}
                  />
                  <span style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)' }}>
                    {m.value}
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {/* #319: Combo error */}
        {comboError && (
          <div
            role="alert"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-3)',
              padding: 'var(--spacing-2)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-sm)',
              background: 'var(--color-surface-muted)',
            }}
          >
            {comboError}
          </div>
        )}

        {/* Value input */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <label
            htmlFor="mask-value"
            style={{
              display: 'block',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              color: 'var(--color-text-secondary)',
              marginBottom: 'var(--spacing-2)',
            }}
          >
            {t('showcase.mask.value')}
          </label>
          <input
            id="mask-value"
            type="text"
            placeholder={currentExample}
            aria-label={t('showcase.mask.value')}
            aria-invalid={!!errors.value}
            aria-describedby={errors.value ? 'mask-value-error' : undefined}
            {...register('value')}
            disabled={isPending}
            style={{
              width: '100%',
              padding: 'var(--spacing-3)',
              border: `1px solid ${errors.value ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontFamily: 'var(--font-family-mono)',
              background: isPending ? 'var(--color-surface-muted)' : 'var(--color-surface-background)',
              color: 'var(--color-text-primary)',
              boxSizing: 'border-box',
            }}
          />
          {errors.value && (
            <p
              id="mask-value-error"
              role="alert"
              style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: 'var(--spacing-1)' }}
            >
              {errors.value.message}
            </p>
          )}
        </div>

        {/* Submit */}
        <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
          <button
            type="submit"
            disabled={isPending || !isValid}
            aria-busy={isPending}
            style={{
              padding: 'var(--spacing-3) var(--spacing-6)',
              background: isPending ? 'var(--color-text-muted)' : 'var(--color-brand-primary)',
              color: 'var(--color-text-inverse)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 600,
              cursor: isPending ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--font-family-sans)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--spacing-2)',
            }}
          >
            {isPending && (
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  border: '2px solid rgba(255,255,255,0.3)',
                  borderTopColor: 'var(--color-text-inverse)',
                  borderRadius: '50%',
                  display: 'inline-block',
                  animation: 'spin 0.8s linear infinite',
                }}
              />
            )}
            {isPending ? t('showcase.mask.loading') : t('showcase.mask.execute')}
          </button>
          {isSuccess && (
            <button
              type="button"
              onClick={() => reset()}
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: 'transparent',
                color: 'var(--color-text-secondary)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family-sans)',
              }}
            >
              {t('showcase.common.reset')}
            </button>
          )}
        </div>
      </form>

      {/* Result card */}
      {isSuccess && data && (
        <div
          role="region"
          aria-label={t('showcase.mask.resultLabel')}
          style={{
            border: '1px solid var(--color-success)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-3)' }}>
            <div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>
                {t('showcase.mask.result.original')}
              </p>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
                {data.original}
              </code>
            </div>
            <div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 'var(--spacing-1)' }}>
                {t('showcase.mask.result.masked')}
              </p>
              <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-sm)', color: 'var(--color-brand-primary)', wordBreak: 'break-all', fontWeight: 700 }}>
                {data.masked}
              </code>
            </div>
          </div>
          <div style={{ marginTop: 'var(--spacing-3)', display: 'flex', gap: 'var(--spacing-4)' }}>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              type: <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-secondary)' }}>{data.type}</code>
            </span>
            <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              mode: <code style={{ fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-secondary)' }}>{data.mode}</code>
            </span>
          </div>
        </div>
      )}

      {/* #319: Better error display — surface BE error.code + message */}
      {isError && displayError && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-4)',
            background: 'var(--color-surface-muted)',
            marginBottom: 'var(--spacing-4)',
            color: 'var(--color-danger)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          <strong>{displayError.code}</strong>: {displayError.message}
        </div>
      )}
    </section>
  );
}
